import * as Sentry from "@sentry/react";

const dsn = import.meta.env["VITE_SENTRY_DSN"] as string | undefined;

const SENSITIVE_BODY_KEYS = new Set([
  "password",
  "currentPassword",
  "newPassword",
  "confirmPassword",
  "token",
  "resetToken",
  "inviteToken",
  "apiKey",
  "secret",
]);

function scrubObject(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(scrubObject);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_BODY_KEYS.has(k)) {
      out[k] = "[Filtered]";
    } else if (v && typeof v === "object") {
      out[k] = scrubObject(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

const IGNORED_ERROR_MESSAGES: (string | RegExp)[] = [
  "AbortError",
  "The operation was aborted",
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /NetworkError when attempting to fetch resource/i,
  "Load failed",
];

const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^safari-web-extension:\/\//i,
];

function isExpectedClientError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as {
    name?: string;
    status?: number;
    statusCode?: number;
    response?: { status?: number };
  };
  if (err.name === "AbortError") return true;
  const status = err.status ?? err.statusCode ?? err.response?.status;
  if (typeof status === "number" && status >= 400 && status < 500) return true;
  return false;
}

if (dsn) {
  Sentry.init({
    dsn,
    environment:
      (import.meta.env["VITE_SENTRY_ENVIRONMENT"] as string | undefined) ??
      import.meta.env.MODE,
    release: import.meta.env["VITE_SENTRY_RELEASE"] as string | undefined,
    tracesSampleRate: 0,
    sendDefaultPii: false,
    ignoreErrors: IGNORED_ERROR_MESSAGES,
    denyUrls: DENY_URLS,
    beforeSend(event, hint) {
      if (isExpectedClientError(hint?.originalException)) {
        return null;
      }
      if (event.request) {
        const req = event.request as Record<string, unknown> & {
          headers?: Record<string, unknown>;
          cookies?: unknown;
          data?: unknown;
        };
        if (req.cookies) req.cookies = "[Filtered]";
        if (req.headers && typeof req.headers === "object") {
          for (const h of ["cookie", "Cookie", "authorization", "Authorization"]) {
            if (h in req.headers) (req.headers as Record<string, unknown>)[h] = "[Filtered]";
          }
        }
        if (req.data && typeof req.data === "object") {
          req.data = scrubObject(req.data);
        }
      }
      return event;
    },
  });
}

export type SentryActor = {
  id: number;
  email: string;
  name: string;
  role: string;
};

export function setSentryUser(actor: SentryActor | null): void {
  if (!dsn) return;
  if (actor) {
    Sentry.setUser({
      id: String(actor.id),
      email: actor.email,
      username: actor.name,
      role: actor.role,
    });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
